import { useId, type ReactNode, type SelectHTMLAttributes } from 'react';
import { CaretDown, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  helperText?: ReactNode;
  error?: string;
  leadingIcon?: PhosphorIcon;
  containerClassName?: string;
  /** `filled` is the grey, 44px field used by the staff Settings forms (Figma A16). */
  variant?: 'outlined' | 'filled';
}

/** Native <select> dressed like Input (same height, radius, label, focus ring). The platform picker stays: it is accessible everywhere. */
export function Select({
  label,
  helperText,
  error,
  leadingIcon,
  containerClassName,
  className,
  id,
  disabled,
  children,
  variant = 'outlined',
  ...props
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const messageId = `${selectId}-message`;
  const message = error ?? helperText;

  return (
    <div className={cx('flex flex-col gap-(--mbm-space-2)', containerClassName)}>
      {label ? (
        <label
          htmlFor={selectId}
          className={cx('text-label-medium', variant === 'filled' ? 'text-text-primary' : 'text-text-secondary')}
        >
          {label}
        </label>
      ) : null}
      <div
        className={cx(
          'relative flex items-center gap-(--mbm-space-2-5) border px-(--mbm-space-4)',
          variant === 'filled'
            ? 'h-(--mbm-size-input-filled) rounded-(--mbm-radius-sm) bg-surface-tint'
            : 'h-(--mbm-size-input) rounded-(--mbm-component-radius-input) bg-surface-card',
          'focus-within:outline-2 focus-within:-outline-offset-2',
          error
            ? 'border-feedback-error focus-within:outline-feedback-error'
            : cx(
                variant === 'filled' ? 'border-transparent' : 'border-border-default',
                'focus-within:outline-focus-ring',
              ),
          disabled && 'bg-surface-tint opacity-(--mbm-opacity-disabled)',
        )}
      >
        {leadingIcon ? (
          <span className="text-text-tertiary">
            <Icon icon={leadingIcon} size="md" />
          </span>
        ) : null}
        <select
          id={selectId}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          className={cx(
            'h-full min-w-0 flex-1 appearance-none bg-transparent pr-(--mbm-space-6) text-text-primary outline-none',
            // The filled field is the staff forms' 14px one (Figma A16, A05.11b); the outlined one is the 17px of the auth pages.
            variant === 'filled' ? 'text-body-compact' : 'text-body-large',
            className,
          )}
          {...props}
        >
          {children}
        </select>
        <span aria-hidden className="pointer-events-none absolute right-(--mbm-space-4) text-text-tertiary">
          <Icon icon={CaretDown} size="md" />
        </span>
      </div>
      {message ? (
        <p
          id={messageId}
          role={error ? 'alert' : undefined}
          className={cx('text-body-small', error ? 'text-feedback-error' : 'text-text-tertiary')}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}
